// src/components/CompanyMembersModal.tsx
import React, { useState, useEffect, useCallback } from "react";
import { FiX, FiUser, FiSearch, FiPlus, FiShield, FiUsers, FiTrash2 } from "react-icons/fi";
import { toast } from "react-hot-toast";
import { confirm } from "../../lib/dialogs";
import { 
  getCompanyMembers, 
  addCompanyMember, 
  updateCompanyMemberRole, 
  removeCompanyMember,
  searchUsers 
} from "../../services/roles";
import { useUserRole } from "../../hooks/useUserRole";
import { useSupabase } from "../../hooks/useSupabase";
import type { Company, CompanyMember, UserProfile, UserRole } from "../../types/domain";
import "../CompanyMembersModal.css";

interface Props {
  company: Company | null;
  isOpen: boolean;
  onClose: () => void;
}

const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: "Super Admin",
  admin: "Administrador",
  member: "Miembro"
};

const ROLE_COLORS: Record<UserRole, string> = {
  super_admin: "#9C27B0", 
  admin: "#FF5722",
  member: "#4CAF50"
};

export default function CompanyMembersModal({ company, isOpen, onClose }: Props) {
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [availableUsers, setAvailableUsers] = useState<UserProfile[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const { canManageUsers } = useUserRole();
  const { user } = useSupabase();

  // Solo el email específico puede asignar super admins
  const canAssignSuperAdmin = () => {
    return user?.email === 'alvarogds04@gmail.com';
  };

  const loadMembers = useCallback(async () => {
    if (!company) return;
    
    setLoading(true);
    const { data, error } = await getCompanyMembers(company.id);
    if (error) {
      toast.error("Error cargando miembros");
    } else if (data) {
      setMembers(data);
    }
    setLoading(false);
  }, [company]);

  const loadAvailableUsers = useCallback(async () => {
    const { data, error } = await searchUsers(searchQuery);
    if (!error && data) {
      // Filter out users that are already members
      const memberUserIds = new Set(members.map(m => m.user_id));
      setAvailableUsers(data.filter(user => !memberUserIds.has(user.id)));
    }
  }, [searchQuery, members]);

  useEffect(() => {
    if (isOpen && company) {
      loadMembers();
      loadAvailableUsers();
    }
  }, [isOpen, company, loadMembers, loadAvailableUsers]);


  useEffect(() => {
    if (showAddMember) {
      loadAvailableUsers();
    }
  }, [searchQuery, showAddMember, loadAvailableUsers]);

  const handleAddMember = async (userId: string, role: UserRole) => {
    if (!company) return;

    try {
      const { data, error } = await addCompanyMember(company.id, userId, role);
      
      if (error) {
        console.error("Error adding member:", error);
        // Provide more specific error messages
        if (error.code === '23505') {
          toast.error("El usuario ya es miembro de esta empresa");
        } else if (error.message) {
          toast.error(`Error: ${error.message}`);
        } else {
          toast.error("Error añadiendo miembro");
        }
        return;
      }

      if (data) {
        setMembers(prev => [...prev, data]);
        setShowAddMember(false);
        setSearchQuery("");
        toast.success("Miembro añadido exitosamente");
        // Refresh available users to remove the added user
        loadAvailableUsers();
      }
    } catch (error) {
      console.error("Error adding member:", error);
      toast.error(`Error añadiendo miembro: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: UserRole) => {
    try {
      const { data, error } = await updateCompanyMemberRole(memberId, newRole);
      if (error) throw error;

      if (data) {
        setMembers(prev => prev.map(m => m.id === memberId ? data : m));
        toast.success("Rol actualizado");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error("Error actualizando rol");
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    const confirmed = await confirm({
      title: "Eliminar miembro",
      text: "¿Estás seguro de eliminar este miembro de la empresa?",
      icon: "warning",
      confirmText: "Eliminar",
    });
    
    if (!confirmed) return;

    try {
      const { error } = await removeCompanyMember(memberId);
      if (error) throw error;

      setMembers(prev => prev.filter(m => m.id !== memberId));
      toast.success("Miembro eliminado");
    } catch (error) {
      console.error("Error removing member:", error);
      toast.error("Error eliminando miembro");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="members-modal" onClick={e => e.stopPropagation()}>
        <div className="members-modal-header">
          <h2>
            <FiUsers size={20} />
            Miembros de {company?.name}
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>

        <div className="members-modal-content">
          {/* Add Member Section */}
          <div className="add-member-section">
            {!showAddMember ? (
              <button 
                className="add-member-btn"
                onClick={() => setShowAddMember(true)}
              >
                <FiPlus size={16} />
                Añadir Miembro
              </button>
            ) : (
              <div className="add-member-form">
                <div className="search-section">
                  <div className="search-input-container">
                    <FiSearch size={16} />
                    <input
                      type="text"
                      placeholder="Buscar usuarios por email o nombre..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                  </div>
                  <button 
                    className="cancel-add-btn"
                    onClick={() => {
                      setShowAddMember(false);
                      setSearchQuery("");
                    }}
                  >
                    Cancelar
                  </button>
                </div>

                <div className="available-users">
                  {availableUsers.map(user => (
                    <div key={user.id} className="user-item">
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.full_name || ""} />
                          ) : (
                            <FiUser size={16} />
                          )}
                        </div>
                        <div className="user-details">
                          <span className="user-name">
                            {user.full_name || "Sin nombre"}
                          </span>
                          <span className="user-email">{user.email}</span>
                        </div>
                      </div>
                      <div className="role-actions">
                        <button 
                          className="role-btn member"
                          onClick={() => handleAddMember(user.id, "member")}
                        >
                          + Miembro
                        </button>
                        <button 
                          className="role-btn admin"
                          onClick={() => handleAddMember(user.id, "admin")}
                        >
                          + Admin
                        </button>
                        {canAssignSuperAdmin() && (
                          <button 
                            className="role-btn super-admin"
                            onClick={() => handleAddMember(user.id, "super_admin")}
                          >
                            + Super Admin
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {availableUsers.length === 0 && (
                    <div className="no-users">
                      {searchQuery ? "No se encontraron usuarios" : "Cargando usuarios..."}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Members List */}
          <div className="members-list">
            {loading ? (
              <div className="loading">Cargando miembros...</div>
            ) : (
              members.map(member => (
                <div key={member.id} className="member-item">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.user?.avatar_url ? (
                        <img src={member.user.avatar_url} alt={member.user.full_name || ""} />
                      ) : (
                        <FiUser size={20} />
                      )}
                    </div>
                    <div className="member-details">
                      <span className="member-name">
                        {member.user?.full_name || "Sin nombre"}
                      </span>
                      <span className="member-email">{member.user?.email}</span>
                    </div>
                  </div>
                  
                  <div className="member-actions">
                    <div className="role-badge" style={{ backgroundColor: ROLE_COLORS[member.role] }}>
                      <FiShield size={14} />
                      {ROLE_LABELS[member.role]}
                    </div>
                    
                    {canManageUsers(company?.id) && member.user?.role !== "super_admin" && (
                      <div className="action-buttons">
                        <select
                          className="role-select"
                          value={member.role}
                          onChange={e => handleUpdateRole(member.id, e.target.value as UserRole)}
                          disabled={member.role === "super_admin" && !canAssignSuperAdmin()}
                        >
                          <option value="member">Miembro</option>
                          <option value="admin">Admin</option>
                          {canAssignSuperAdmin() && <option value="super_admin">Super Admin</option>}
                        </select>
                        <button
                          className="remove-btn"
                          onClick={() => handleRemoveMember(member.id)}
                          title="Eliminar miembro"
                          disabled={member.role === "super_admin" && !canAssignSuperAdmin()}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}

            {!loading && members.length === 0 && (
              <div className="no-members">
                <FiUsers size={48} />
                <p>Esta empresa no tiene miembros todavía</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}